import _ from "lodash";

import {$store} from "@isudio/globals";
import {config, routes} from "@isudio/config";
import {SignupStatus, UserStatus} from "@isudio/data";
import type {IDevicePayload, ISignupInvite, IUserPayload, IUserSignupResponse, IActivatePayload, IOtpRequestPayload, ISigninPayload, IUserSigninResponse, IChangePasswordPayload, IUser, IRefreshTokenPayload, IUserSession, IUserProfilePayload, IContactPayload, IBriefPayload, IApplyJobPayload, IContactAuthorPayload, ISearch, ISearchResponse} from "@isudio/data";
import {WorkflowActions} from "@isudio/data";
import {$api} from "./api.service";
import {HttpStatus} from "../constants/http.constants";

export class CoreServices {
  constructor() {
  }

  public async getAppData(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.get(routes.getAppData);

      } catch (e: any) {
        res = this.getError(e)
      }

      if (_.get(res, 'status') === HttpStatus.OK) {
        const data: any = _.get(res, 'data');
        if (!_.isNothing(data)) {
          resolve(data);

        } else {
          reject({
            code: "unknown",
            message: "An unexpected error occurred while fetching app data. Please try again later or report if issue persists."
          });
        }

      } else {
        reject(_.get(res, 'error'));
      }
    });
  }

  public async sendInvite(payload: any): Promise<ISignupInvite | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.sendInvite, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: ISignupInvite = _.get(res, 'data');
          if (data.status === SignupStatus.pending) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while sending your signup invite. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async verifyInvite(payload: any): Promise<ISignupInvite | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.get(routes.verifyInvite, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: ISignupInvite = _.get(res, 'data');
          if (data.status === SignupStatus.pending) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while verifying your signup invite. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async idExists(payload: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.get(routes.idExists, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: any = _.get(res, 'data');
          resolve(data);
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async getUser(payload: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.get(routes.getUser, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: any = _.get(res, 'data');
          resolve(data);
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async signup(payload: IUserPayload): Promise<IUserSignupResponse | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.signup, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.CREATED:
          const data: IUserSignupResponse = _.get(res, 'data');
          if (data.status === UserStatus.activationPending) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred during sign up. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async otpRequest(payload: IOtpRequestPayload): Promise<IUserSignupResponse | IUser | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.otpRequest, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: IUserSignupResponse | IUser = _.get(res, 'data');
          if ([UserStatus.activationPending, UserStatus.active].indexOf(data.status as UserStatus) > -1) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while sending your OTP. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async verifyActivation(payload: any): Promise<IUserSignupResponse | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.get(routes.verifyActivation, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: IUserSignupResponse = _.get(res, 'data');
          if (data.status === UserStatus.activationPending) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while verifying activation status of your account. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject({
            ..._.get(res, 'error'),
            data: _.get(res, 'data')
          });
          break;
      }
    });
  }

  public async activate(payload: IActivatePayload): Promise<IUserSignupResponse | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.activate, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: IUserSignupResponse = _.get(res, 'data');
          if (data.status === UserStatus.active) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while activating your account. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async signin(payload: ISigninPayload): Promise<IUserSigninResponse | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.signin, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.ACCEPTED:
          const data: IUserSigninResponse = _.get(res, 'data');
          if ((_.get(data, 'user.status') === UserStatus.active) &&
            (!_.isNothing(data, 'session.accessToken.token') && !_.isNothing(data, 'session.refreshToken.token'))) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while signing in. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async verifyChangePassword(payload: any): Promise<IUser | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.get(routes.verifyChangePassword, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: IUser = _.get(res, 'data');
          if (data.status === UserStatus.active) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while verifying password change request for your account. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject({
            ..._.get(res, 'error'),
            data: _.get(res, 'data')
          });
          break;
      }
    });
  }

  public async sendChangePasswordOtp(id: string): Promise<IUserSignupResponse | IUser | any> {
    const payload: IOtpRequestPayload = _.omitBy({
      id,
      action: WorkflowActions.changePassword,
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IOtpRequestPayload;

    return this.otpRequest(payload);
  }

  public async changePassword(payload: IChangePasswordPayload): Promise<IUser | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.changePassword, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: IUser = _.get(res, 'data');
          if (data.status === UserStatus.active) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while changing your password. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async saveProfile(payload: IUserProfilePayload): Promise<IUser | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.saveProfile, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: IUser = _.get(res, 'data');
          if (data.status === UserStatus.active) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while saving profile. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async signout(payload: IDevicePayload): Promise<IUser | any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.signout, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const user: IUser = _.get(res, 'data');
          if ((_.get(user, 'status') === UserStatus.active)) {
            resolve(user);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while signing out. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async search(dbName: string, query: string, filters?: Array<any>, includeDocs: boolean = true, limit: number = 100): Promise<ISearchResponse> {
    return new Promise(async (resolve, reject) => {
      const payload: ISearch = {
        dbName,
        query,
        filters,
        includeDocs,
        limit
      }

      let res: any;
      try {
        res = await $api.post(routes.search, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          const data: ISearchResponse = _.get(res, 'data');
          if (data.status === HttpStatus.OK) {
            resolve(data);

          } else {
            reject({
              code: "unknown",
              message: "An unexpected error occurred while verifying activation status of your account. Please try again later or report if issue persists."
            });
          }
          break;

        default:
          reject({
            ..._.get(res, 'error'),
            data: _.get(res, 'data')
          });
          break;
      }
    });
  }

  public async contact(payload: IContactPayload): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.contact, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          resolve(HttpStatus.OK);
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async brief(payload: IBriefPayload): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.brief, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          resolve(HttpStatus.OK);
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async applyJob(payload: IApplyJobPayload): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.applyJob, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          resolve(HttpStatus.OK);
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  public async contactAuthor(payload: IContactAuthorPayload): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let res: any;
      try {
        res = await $api.post(routes.contactAuthor, payload);

      } catch (e: any) {
        res = this.getError(e)
      }

      switch (_.get(res, 'status')) {
        case HttpStatus.OK:
          resolve(HttpStatus.OK);
          break;

        default:
          reject(_.get(res, 'error'));
          break;
      }
    });
  }

  private getError(e: any): any {
    return _.omitBy({
      status: e?.status || e?.response?.data?.statusCode || null,
      data: e?.response?.data?.data || null,
      error: _.omitBy({
        code: e?.response?.data?.errorCode || e?.response?.data?.statusCode || null,
        message: e?.response?.data?.error || (_.isString(e?.response?.data?.message) ? e?.response?.data?.message : null) ||  null,
        data: (!_.isString(e?.response?.data?.message) ? e?.response?.data?.message : null) || null
      }, _.isNil)
    }, _.isNil);
  }
}

export const $core: CoreServices = new CoreServices();
if (typeof window !== 'undefined' && config.app.debug) (window as any).$core = $core;

export async function getAuthToken(endPoint?: string): Promise<string | null> {
  const session: any = _.get($store, "User.session$.value", null);
  if (!_.isNothing(session)) {
    if (endPoint && endPoint.indexOf(routes.refreshToken) > -1) {
      return session.refreshToken?.token || null;
    } else {
      return session.accessToken?.token || null;
    }
  }
  return null;
}

export async function refreshToken(): Promise<string | null> {
  const session = _.get($store, "User.session$.value", null);
  if (!session) return null;

  const payload: IRefreshTokenPayload = _.omitBy({
    uid: _.get($store, 'User.session.uid'),
    did: _.get($store, 'User.session.did'),
    accessToken: _.get($store, 'User.session.accessToken.token'),
    refreshToken: _.get($store, 'User.session.refreshToken.token')
  }, _.isNil) as IRefreshTokenPayload;

  let res: any;
  try {
    res = await $api.post(routes.refreshToken, payload);

  } catch (e) {
    res = {
      status: _.get(e, "status"),
      data: _.get(e, "response.data.data"),
      error: {
        code: _.get(e, "response.data.errorCode"),
        message: _.get(e, "response.data.message"),
      },
    };
  }

  if (_.get(res, 'status') === HttpStatus.ACCEPTED) {
    const session: IUserSession = _.get(res, 'data');
    if (!_.isNothing(session, 'accessToken.token') && !_.isNothing(session, 'refreshToken.token')) {
      _.get($store, 'User.session$').next(session);
      return session.accessToken.token;
    }
  }

  return null;
}
